import React, { useState, useEffect } from 'react';
import { useAuth, API } from '@/App';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { Brain, Play, Check, Clock, Loader2, ChevronRight, Activity, LogOut } from 'lucide-react';

export function FacilitatorDashboard() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSession, setActiveSession] = useState(null);
  const [notes, setNotes] = useState('');
  const [executing, setExecuting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/terra/facilitator/today`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleExecute = async (sessionId) => {
    setExecuting(true);
    try {
      await axios.put(`${API}/terra/sessions/${sessionId}/execute`, { execution_notes: 'Session started' });
      toast.success('Session started');
      const r = await axios.get(`${API}/terra/facilitator/today`);
      setData(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setExecuting(false); }
  };

  const handleComplete = async (sessionId) => {
    setExecuting(true);
    try {
      await axios.put(`${API}/terra/sessions/${sessionId}/complete`, { execution_notes: notes, subscriber_feedback: '' });
      toast.success('Session completed');
      setActiveSession(null); setNotes('');
      const r = await axios.get(`${API}/terra/facilitator/today`);
      setData(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setExecuting(false); }
  };

  const STATE_COLORS = { stable: '#4E7A6B', unstable: '#D4A843', underdrive: '#5C6B8A', overdrive: '#C87B7B', triggered: '#8C6B55', stressed: '#9B7BC8', recovery_needed: '#C85C5C' };

  return (
    <div className="min-h-screen bg-[#F8F6F3]">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#8A8681] font-['Work_Sans']">Terra Centre</p>
            <h1 className="text-xl font-['Outfit'] font-light text-[#2C2A29]">Facilitator Dashboard</h1>
            <p className="text-xs text-[#8A8681] font-['Work_Sans'] mt-0.5">{user?.name} &middot; {data?.date}</p>
          </div>
          <Button variant="ghost" onClick={logout} className="text-[#8A8681] text-xs"><LogOut className="w-3.5 h-3.5 mr-1" /> Logout</Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-[#C89B7B] animate-spin" /></div>
        ) : !data?.subscribers?.length ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-[#EBE8E3]">
            <Brain className="w-10 h-10 text-[#C89B7B] mx-auto mb-3" strokeWidth={1} />
            <p className="text-sm text-[#5C5955]">No subscribers assigned today</p>
          </div>
        ) : (
          <div className="space-y-6" data-testid="facilitator-subscribers">
            {data.subscribers.map(sub => (
              <div key={sub.subscriber_id} className="bg-white rounded-2xl border border-[#EBE8E3] overflow-hidden" data-testid={`fac-sub-${sub.subscriber_id}`}>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-base font-['Outfit'] text-[#2C2A29]">{sub.name}</h3>
                    {sub.daily_state?.current_state && (
                      <Badge variant="outline" className="text-[9px] capitalize" style={{ borderColor: (STATE_COLORS[sub.daily_state.current_state] || '#8A8681') + '30', color: STATE_COLORS[sub.daily_state.current_state] }}>
                        {sub.daily_state.current_state.replace('_', ' ')}
                      </Badge>
                    )}
                  </div>
                  {sub.arrival_state_summary && (
                    <div className="grid grid-cols-3 gap-2 mb-3 text-[10px]">
                      {sub.arrival_state_summary.severity_score != null && <span className="text-[#8A8681]">Severity: <strong className="text-[#2C2A29]">{sub.arrival_state_summary.severity_score}</strong></span>}
                      {sub.arrival_state_summary.risk_level && <span className="text-[#8A8681]">Risk: <strong style={{ color: sub.arrival_state_summary.risk_level === 'high' ? '#C85C5C' : '#D4A843' }}>{sub.arrival_state_summary.risk_level}</strong></span>}
                      {sub.arrival_state_summary.system_priorities?.length > 0 && <span className="text-[#8C6B55]">{sub.arrival_state_summary.system_priorities[0]?.replace('_', ' ')}</span>}
                    </div>
                  )}
                </div>
                {/* Sessions */}
                {sub.today_sessions?.length > 0 && (
                  <div className="border-t border-[#EBE8E3] px-5 py-3 space-y-2">
                    <p className="text-[10px] uppercase tracking-wider text-[#8A8681]">Today's Sessions</p>
                    {sub.today_sessions.map(s => (
                      <div key={s.session_id} className="flex items-center gap-3 py-2">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${s.status === 'completed' ? 'bg-[#4E7A6B]/10' : 'bg-[#8C6B55]/10'}`}>
                          {s.status === 'completed' ? <Check className="w-4 h-4 text-[#4E7A6B]" /> : <Play className="w-4 h-4 text-[#8C6B55]" />}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-[#2C2A29] font-['Work_Sans']">{s.session_type?.replace('_', ' ')}</p>
                          <p className="text-[10px] text-[#8A8681]">{s.status}</p>
                        </div>
                        {s.status === 'scheduled' && (
                          <Button size="sm" onClick={() => handleExecute(s.session_id)} disabled={executing}
                            className="bg-[#8C6B55] text-white hover:bg-[#7C5B45] text-xs rounded-full px-4" data-testid={`start-${s.session_id}`}>
                            Start
                          </Button>
                        )}
                        {s.status === 'in_progress' && (
                          <div className="flex items-center gap-2">
                            <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes..." className="h-8 text-xs w-40 bg-white border-[#EBE8E3]" />
                            <Button size="sm" onClick={() => handleComplete(s.session_id)} disabled={executing}
                              className="bg-[#4E7A6B] text-white hover:bg-[#3E6A5B] text-xs rounded-full px-4" data-testid={`complete-${s.session_id}`}>
                              Complete
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function TerraHeadDashboard() {
  const { user, logout } = useAuth();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const cityId = user?.tenant_scope?.city_id || 'ahmedabad';

  useEffect(() => {
    axios.get(`${API}/terra/city/${cityId}/overview`).then(r => setOverview(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [cityId]);

  return (
    <div className="min-h-screen bg-[#F8F6F3]">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#8A8681] font-['Work_Sans']">Terra Centre</p>
            <h1 className="text-xl font-['Outfit'] font-light text-[#2C2A29]">City Operations</h1>
            <p className="text-xs text-[#8A8681] font-['Work_Sans'] mt-0.5">{user?.name} &middot; {cityId}</p>
          </div>
          <Button variant="ghost" onClick={logout} className="text-[#8A8681] text-xs"><LogOut className="w-3.5 h-3.5 mr-1" /> Logout</Button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-[#C89B7B] animate-spin" /></div>
        ) : overview ? (
          <div className="grid grid-cols-2 gap-4" data-testid="terra-head-overview">
            {[
              { label: 'Centres', value: overview.centres, color: '#5C6B8A' },
              { label: 'Active Subscribers', value: overview.active_subscribers, color: '#4E7A6B' },
              { label: 'Today Sessions', value: overview.today_sessions, color: '#C89B7B' },
              { label: 'Completed Today', value: overview.completed_today, color: '#8C6B55' },
            ].map(m => (
              <div key={m.label} className="bg-white rounded-2xl border border-[#EBE8E3] p-6 text-center">
                <p className="text-3xl font-['Outfit'] font-light" style={{ color: m.color }}>{m.value}</p>
                <p className="text-[10px] uppercase tracking-wider text-[#8A8681] mt-1">{m.label}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-white rounded-2xl border border-[#EBE8E3]">
            <p className="text-sm text-[#8A8681]">No data available for this city</p>
          </div>
        )}
      </div>
    </div>
  );
}

export function TerraInvestorDashboard() {
  const { user, logout } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const cityId = user?.tenant_scope?.city_id || 'ahmedabad';

  useEffect(() => {
    axios.get(`${API}/terra/investor/${cityId}/analytics`).then(r => setAnalytics(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [cityId]);

  return (
    <div className="min-h-screen bg-[#F8F6F3]">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#8A8681] font-['Work_Sans']">Terra Centre</p>
            <h1 className="text-xl font-['Outfit'] font-light text-[#2C2A29]">City Analytics</h1>
            <p className="text-xs text-[#8A8681] font-['Work_Sans'] mt-0.5">{user?.name} &middot; {cityId}</p>
          </div>
          <Button variant="ghost" onClick={logout} className="text-[#8A8681] text-xs"><LogOut className="w-3.5 h-3.5 mr-1" /> Logout</Button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-[#C89B7B] animate-spin" /></div>
        ) : analytics ? (
          <div className="space-y-4" data-testid="investor-analytics">
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Total Centres', value: analytics.total_centres },
                { label: 'Active Subscribers', value: analytics.total_active_subscribers },
                { label: 'Sessions (30d)', value: analytics.total_sessions_30d },
                { label: 'Completion Rate', value: `${analytics.completion_rate}%` },
              ].map(m => (
                <div key={m.label} className="bg-white rounded-2xl border border-[#EBE8E3] p-6 text-center">
                  <p className="text-3xl font-['Outfit'] font-light text-[#2C2A29]">{m.value}</p>
                  <p className="text-[10px] uppercase tracking-wider text-[#8A8681] mt-1">{m.label}</p>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-[#8A8681] text-center font-['Work_Sans'] italic">Aggregate data only — no individual subscriber information</p>
          </div>
        ) : (
          <div className="text-center py-16 bg-white rounded-2xl border border-[#EBE8E3]">
            <p className="text-sm text-[#8A8681]">No analytics available</p>
          </div>
        )}
      </div>
    </div>
  );
}
